import React from 'react';
import { Box } from '@chakra-ui/react';
import { UserGroupIcons, SwitchArrowIcon } from '@components/common';

import { CodePreview } from './code-preview';
import { useRouter } from 'next/router';

type Component = {
  name: string;
  description: string;
  icon: (props: React.SVGProps<SVGSVGElement>) => JSX.Element;
  image: string;
};

export function B2BComponents(): JSX.Element {
  const componentList = React.useMemo<Component[]>(
    () => [
      {
        name: '<OrganizationProfile/>',
        description:
          'Enable organization admins to manage organization memberships, invite new members, switch roles, and change organization settings.',
        icon: UserGroupIcons,
        image: '/images/components/org-members.svg'
      },
      {
        name: '<OrganizationSwitcher/>',
        description:
          'Enable users to easily create new organizations, and switch between their organizations.',
        icon: SwitchArrowIcon,
        image: '/images/components/org-switcher.png'
      }
    ],
    []
  );

  const [currentFramework, setCurrentFramework] =
    React.useState<string>('Next.js');
  const [currentComponent, setCurrentComponent] = React.useState<Component>(
    componentList[0]
  );
  const [snippet, setSnippet] = React.useState<string>();
  const [component, setComponent] = React.useState<string>();
  const { query } = useRouter();

  React.useEffect(() => {
    if (query.component) {
      if (query.component === 'OrganizationProfile') {
        setCurrentComponent(componentList[0]);
      } else if (query.component === 'OrganizationSwitcher') {
        setCurrentComponent(componentList[1]);
      }
    }
  }, [query.component, componentList]);

  const sdkImport: string = React.useMemo(() => {
    switch (currentFramework) {
      case 'React':
      case 'Redwood':
      case 'Gatsby':
        return 'clerk-react';
      case 'Next.js':
        return 'nextjs';
      case 'Remix':
        return 'remix';
      default:
        return 'clerk-react';
    }
  }, [currentFramework]);

  React.useEffect(() => {
    if (query.component) {
      if (query.component === 'OrganizationProfile') {
        setCurrentComponent(componentList[0]);
      } else if (query.component === 'OrganizationSwitcher') {
        setCurrentComponent(componentList[1]);
      }
    }
  }, [query.component, componentList]);

  React.useEffect(() => {
    const componentSwitcher = () => {
      switch (currentComponent.name) {
        case '<OrganizationSwitcher/>':
          return setComponent('OrganizationSwitcher');
        default:
          return setComponent('OrganizationProfile');
      }
    };

    if (currentComponent) {
      componentSwitcher();
    }
  }, [currentComponent]);

  React.useEffect(() => {
    if (component && currentFramework) {
      setSnippet(
        `// ${currentFramework} app
import { ${component} } from '@clerk/${sdkImport}';
           
export default function Page() {
  return <${component} />;
}`
      );
    }
  }, [component, currentFramework, sdkImport]);

  return (
    <Box w="full">
      <CodePreview
        componentList={componentList}
        setCurrentComponent={setCurrentComponent}
        currentComponent={currentComponent}
        currentFramework={currentFramework}
        setCurrentFramework={setCurrentFramework}
        snippet={snippet}
        header={'B2B SaaS Components'}
        subheader={'Create and manage organizations and their members'}
        imgDirection="left"
        icon={'/images/components/integrate-icon.svg'}
      />
    </Box>
  );
}
